import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { companionAppsService } from 'GeminiViewerComponent/features/services/companionApp.service';

const initialState = {
    loadingStatus: LoadingStatus.Idle,
    allCompanionApps: [],
    CompanionApp: {},
    loadedRowsMap: {},
    pageInfo: {},
    companionUsage: [],
    error: null,
};

export const fetchAllCompanionApps = createAsyncThunk('companionApps/fetchAllCompanionApps', async (params) => {
    const response = await companionAppsService.getAll(params.searchString, params.sort);
    return response;
});

export const fetchCompanionAppById = createAsyncThunk('companionApps/fetchCompanionAppById', async (params) => {
    const response = await companionAppsService.getById(params);
    return response;
});

export const createCompanionApp = createAsyncThunk('companionApps/createCompanionApp', async (params) => {
    const response = await companionAppsService.create(params);
    return response;
});

export const editCompanionApp = createAsyncThunk('companionApps/editCompanionApp', async (params) => {
    const response = await companionAppsService.update(params.companion_app_id, params);
    return response;
});

export const deleteCompanionApp = createAsyncThunk('companionApps/deleteCompanionApp', async (params) => {
    const response = await companionAppsService.delete(params);
    return response;
});

export const deleteCompanionApps = createAsyncThunk('companionApps/deleteCompanionApps', async (params) => {
    const response = await companionAppsService.deleteArray(params);
    return response;
});

export const fetchCompanionAppUsageById = createAsyncThunk(
    'companionApps/fetchCompanionAppUsageById',
    async (params) => {
        const response = await companionAppsService.fetchUsageById(params);
        return response;
    }
);

const companionAppsSlice = createSlice({
    name: 'companionApps',
    initialState,
    reducers: {
        resetCompanionAppStatus: (state) => {
            return initialState;
        },
        setCompanionApps: (state, action) => {
            state.allCompanionApps = action.payload ?? [];
        },
        setCompanionApp: (state, action) => {
            state.CompanionApp = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAllCompanionApps.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Loaded;
                state.allCompanionApps = action.payload.companionApps ?? [];
                state.pageInfo = action.payload.pageInfo;
            })
            .addCase(fetchAllCompanionApps.rejected, (state, action) => {
                state.error = action.error.message;
            })
            .addCase(fetchCompanionAppById.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Loaded;
                state.CompanionApp = action.payload;
            })
            .addCase(fetchCompanionAppById.rejected, (state, action) => {
                state.error = action.error.message;
            })
            .addCase(createCompanionApp.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Idle;
            })
            .addCase(createCompanionApp.rejected, (state, action) => {
                state.error = action.error.message;
            })
            .addCase(editCompanionApp.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Idle;
            })
            .addCase(editCompanionApp.rejected, (state, action) => {
                state.error = action.error.message;
            })
            .addCase(deleteCompanionApp.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Idle;
            })
            .addCase(deleteCompanionApp.rejected, (state, action) => {
                state.error = action.error.message;
            })
            .addCase(deleteCompanionApps.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Idle;
            })
            .addCase(deleteCompanionApps.rejected, (state, action) => {
                state.error = action.error.message;
            })
            .addCase(fetchCompanionAppUsageById.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Loaded;
                state.companionUsage = action.payload;
            })
            .addCase(fetchCompanionAppUsageById.rejected, (state, action) => {
                state.error = action.error.message;
            });
    },
});

export const getCompanionLoadingStatus = (state) => state.companionApps.loadingStatus;
export const getAllCompanionApps = (state) => state.companionApps.allCompanionApps;
export const getCompanionApp = (state) => state.companionApps.CompanionApp;
export const getCompanionPageInfo = (state) => state.companionApps.pageInfo;
export const getCompanionAppUsage = (state) => state.companionApps.companionUsage;

export const { resetCompanionAppStatus, setCompanionApps, setCompanionApp } = companionAppsSlice.actions;

export default companionAppsSlice.reducer;
